import { PolymerElement, html } from '@polymer/polymer';
import { KatapultEditorTemplate } from './katapult-editor-template';
import '@polymer/iron-icon/iron-icon.js';
import { NameToKey } from '../../modules/NameToKey';
import { Path } from '../../modules/Path.js';
import { ContainsIllegalCharacters } from '../../modules/ContainsIllegalCharacters.js';
import { ToArray } from '../../modules/ToArray.js';
import '../katapult-elements/katapult-button.js';

class KatapultCustomIconsEditor extends KatapultEditorTemplate {
  static get template() {
    return html`
      ${super.style}
      <style>
        iron-icon#iconPreview {
          --iron-icon-width: 48px;
          --iron-icon-height: 48px;
        }
        .warningText {
          color: var(--paper-red-600);
        }
      </style>

      <katapult-firebase-worker
        id="iconCollectionsFirebaseWorker"
        path="[[modelSpaceDetails.path]]/models/custom_icon_collections"
        data="{{iconCollections}}"
      >
      </katapult-firebase-worker>

      <paper-dialog
        id="editIconCollections"
        style="width:460px;"
        entry-animation="scale-up-animation"
        exit-animation="fade-out-animation"
        no-cancel-on-outside-click=""
      >
        <div title secondary-color>Edit Icon Collections</div>
        <div body>
          <paper-table style="overflow: hidden;" white outline rounded dividers>
            <paper-table-scroll on-sort-changed="iconCollectionsArraySortChanged">
              <template is="dom-repeat" items="[[iconCollectionsArray]]">
                <paper-row slot="sortable" item="[[item]]">
                  <paper-cell style="background-color:white;">
                    <iron-icon icon="katapult-misc:drag-indicator"></iron-icon>
                    <div style="flex-grow:1; white-space: nowrap;">[[camelCaseKey(item)]]</div>
                    <katapult-button icon="delete" on-click="removeIconCollection"></katapult-button>
                  </paper-cell>
                </paper-row>
              </template>
            </paper-table-scroll>
          </paper-table>
          <paper-row>
            <paper-cell>
              <paper-input
                id="newIconCollectionNameInput"
                label="New Icon Collection"
                value="{{newIconCollectionName}}"
                autofocus
                no-label-float
              ></paper-input>
            </paper-cell>
            <paper-cell icon-button>
              <katapult-button color="white" iconOnly noBorder icon="add" on-click="addNewIconCollection"></katapult-button>
            </paper-cell>
          </paper-row>
        </div>
        <div buttons>
          <katapult-button white outline dialog-dismiss>Close</katapult-button>
        </div>
      </paper-dialog>

      <template is="dom-repeat" items="[[steps]]" as="step">
        <katapult-editor-step
          step$="[[step.id]]"
          creating-item="[[creatingItem]]"
          active="[[step.active]]"
          index="[[step.index]]"
          current-index="[[currentIndex]]"
          title="[[step.title]]"
        >
          <template is="dom-if" if="[[equal(step.id, 'file_upload')]]" restamp>
            <paper-table light-grey style="width: 430px;" rounded outline slot="body">
              <paper-row expand>
                <paper-cell icon>
                  <iron-icon icon="info"></iron-icon>
                </paper-cell>
                <paper-cell style="gap: 8px;" column align-start>
                  <div>To control the color of your icon, use only paths with no fill in your SVG.</div>
                  <div>Your icon should be 24x24 with a view box of 24x24.</div>
                  <div>Design your SVG on a square canvas with no whitespace around the icon.</div>
                </paper-cell>
              </paper-row>
              <paper-row input>
                <paper-cell>
                  <input id="fileInput" type="file" on-change="inputChanged" />
                </paper-cell>
                <paper-cell>
                  <template is="dom-if" if="{{_itemData.svg}}">
                    <iron-icon id="iconPreview" src="[[computeIconSrc(_itemData.svg)]]"></iron-icon>
                  </template>
                </paper-cell>
              </paper-row>
              <template is="dom-if" if="{{inputInvalid}}">
                <paper-row>
                  <paper-cell>
                    <label class="warningText" for="fileInput"
                      >The uploaded SVG was invalid. Please upload a valid SVG file under {{allowedSizeInKB}}KB</label
                    >
                  </paper-cell>
                </paper-row>
              </template>
            </paper-table>
          </template>

          <template is="dom-if" if="[[equal(step.id, 'icon_collection')]]" restamp>
            <katapult-button style="margin-left: 16px;" white outline slot="current-step-body" on-click="nextStep">Skip</katapult-button>
            <paper-table light-grey style="width: 430px;" rounded outline slot="body">
              <paper-row input>
                <paper-cell>
                  <katapult-drop-down
                    items="[[iconCollections]]"
                    label-function="[[camelCaseKey]]"
                    value-path="$key"
                    label="Select an Icon Collection"
                    value="{{_itemData.icon_collection}}"
                    no-label-float
                  >
                    <paper-item on-click="editIconCollections">EDIT ICON COLLECTIONS</paper-item>
                  </katapult-drop-down>
                </paper-cell>
              </paper-row>
            </paper-table>
          </template>
        </katapult-editor-step>
      </template>
      ${super.createItemContainer}
    `;
  }

  static get properties() {
    return {
      inputInvalid: {
        type: Boolean,
        value: false
      },
      allowedSizeInKB: {
        type: Number,
        value: 3
      },
      // Only allow names that can pass as valid CSS selectors. So only allow
      // letters, numbers, and spaces. And the name must start with a leter.
      // Spaces will be replaced with underscores.
      validNameRegex: {
        type: String,
        value: '^[a-zA-Z]+[a-zA-Z0-9 ]*$'
      }
    };
  }

  static get observers() {
    return [
      'updateIconCollectionsArray(iconCollections.*)',
      'computeIconProperty(_itemData.$key)',
      'updateSVGID(_itemData.$key, _itemData.svg)'
    ];
  }

  ready() {
    super.ready();

    this.keyMatchLabel = true;

    // An ordered list of all possible steps.
    this.setSteps([
      {
        id: 'file_upload',
        title: 'Upload an SVG File',
        trigger: () => this.get('_itemData.svg') != null
      },
      {
        id: 'icon_collection',
        title: 'Icon Collection',
        trigger: () => this.get('_itemData.icon_collection') != null
      }
    ]);
  }

  computeIconProperty(key) {
    if (key) {
      this.set(`_itemData.icon`, `model-icons:${key}`);
    }
  }

  updateSVGID(key, svg) {
    if (key && svg) {
      let xmlDoc = new DOMParser().parseFromString(svg, 'image/svg+xml');
      // Set the id based on the key on the SVG tag
      xmlDoc.querySelector('svg').id = key;
      // Set the SVG data back on the item
      this.set(`_itemData.svg`, new XMLSerializer().serializeToString(xmlDoc));
    }
  }

  computeIconSrc(svgData) {
    return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgData);
  }

  editIconCollections(e) {
    e.currentTarget.parentElement.close();
    this.$.editIconCollections.open();
  }

  updateIconCollectionsArray() {
    this.iconCollectionsArray = ToArray(this.iconCollections).sort((a, b) => Path.get(a, 'priority') - Path.get(b, 'priority'));
  }

  checkNameForError(name) {
    name = (name || '').trim();
    let exists = name.length > 0;
    let illegal = ContainsIllegalCharacters(name) || new RegExp(this.validNameRegex).test(name) == false;
    return !exists ? 'You must provide a name' : illegal ? `You may only use alphabetic characters, numbers, and spaces.` : null;
  }

  sanitizeName(name) {
    return NameToKey((name || '').trim());
  }

  addNewIconCollection() {
    let error = this.checkNameForError(this.newIconCollectionName);
    this.$.newIconCollectionNameInput.errorMessage = error;
    this.$.newIconCollectionNameInput.invalid = error != null;
    if (error == null) {
      let sanitized = this.sanitizeName(this.newIconCollectionName);
      this.iconCollectionsArray.push({ $key: sanitized });
      this.saveIconCollections();
      this.newIconCollectionName = '';
    }
  }

  iconCollectionsArraySortChanged(e) {
    let removedItem = this.iconCollectionsArray.splice(e.detail.prevIndex, 1)[0];
    this.iconCollectionsArray.splice(e.detail.index, 0, removedItem);
    this.notifySplices(`iconCollectionsArray`, [
      { index: e.detail.prevIndex, removed: [removedItem], addedCount: 0 },
      { index: e.detail.index, removed: [], addedCount: 1 }
    ]);
    this.saveIconCollections();
  }

  async saveIconCollections() {
    let update = {};
    this.iconCollectionsArray.forEach((item, i) => {
      update[item.$key] = { priority: i, '.priority': i };
    });
    await this.$.iconCollectionsFirebaseWorker._ref.update(update);
  }

  async removeIconCollection(e) {
    await this.$.iconCollectionsFirebaseWorker._ref.child(e.model.item.$key).remove();
  }

  checkSVGValid(svg) {
    try {
      let xmlDoc = new DOMParser().parseFromString(svg, 'image/svg+xml');
      // Check that the svg is valid XML
      if (xmlDoc.getElementsByTagName('parsererror').length > 0) {
        return false;
      }
      // Check that there are no script tags
      if (xmlDoc.getElementsByTagName('script').length > 0) {
        return false;
      }
      // Check that an SVG tag exists
      if (xmlDoc.getElementsByTagName('svg').length == 0) {
        return false;
      }
    } catch (e) {
      return false;
    }
    return true;
  }

  async inputChanged(e) {
    this.inputInvalid = false;
    let reader = new FileReader();
    let fileInput = this.shadowRoot.querySelector('#fileInput');
    let file = Array.from(fileInput?.files)[0];
    // Check if the file is an SVG and under 3 KB
    if (file.type == 'image/svg+xml' && file.size < this.allowedSizeInKB * 1000) {
      reader.onload = () => {
        let svg = reader.result;
        // Check if the SVG is valid
        if (this.checkSVGValid(svg)) {
          this.set(`_itemData.svg`, reader.result);
          this.set(`_itemData.size`, file.size);
        } else {
          this.inputInvalid = true;
        }
        // Remove the file from the input
        fileInput.value = null;
      };
      reader.readAsText(file);
    } else {
      this.inputInvalid = true;
    }
  }
}

customElements.define('katapult-custom-icons-editor', KatapultCustomIconsEditor);
